import React from "react";
import _ from "lodash";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { Typography, Card } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { RestrictionNoticeSearchResult } from "../../constants/interfaces";
import formatDate from "../../utils/formatDate";
import { Icon } from "../../components";
import { Message } from "../../components/Messages";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(4)
  },
  card: {
    marginTop: theme.spacing(4),
    padding: "32px 32px 0 32px"
  },
  prescriber: {
    display: "flex",
    width: "100%",
    marginRight: "1.5em"
  },
  guide: {
    marginTop: "1.5em"
  }
}));

type Props = {
  searchResult: RestrictionNoticeSearchResult | null;
};

const RestrictionNotice: React.FC<Props> = ({ searchResult }) => {
  const classes = useStyles();

  if (!searchResult)
    return (
      <Message
        className={classes.guide}
        message="Search for an individual to get started"
        variant="info"
      />
    );

  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <Section label="Individual">
          <NoticeCell label="Given name" text={searchResult.givenName} />
          <NoticeCell
            label="Middle name(s)"
            text={searchResult.middleName || ""}
          />
          <NoticeCell label="Surname" text={searchResult.surname} />
          <NoticeCell label="NHI" text={searchResult.nhinumber} />
        </Section>
      </Card>
      {searchResult.restrictionNotices.map(notice => {
        const {
          cmAcmCaseIdentifier,
          dateIssued,
          controlledMedicines,
          rinumber,
          pharmacies,
          otherServiceProviders,
          prescribers
        } = notice;

        return (
          <Card key={cmAcmCaseIdentifier} className={classes.card}>
            <Section label="Regulatory Instrument">
              <NoticeCell
                label="Regulatory Instrument Number"
                text={rinumber}
              />
              <NoticeCell
                label="Date issued"
                format={formatDate.condensed}
                text={dateIssued}
              />
            </Section>

            <Section
              hide={!controlledMedicines}
              label="The Medical Officer of Health, Medicines Control hereby prohibits every practitioner from prescribing or any person from supplying the following Controlled Medicines to the named individual"
            >
              {controlledMedicines}
            </Section>

            <Section
              label="Except for prescriptions or supplies given by the following medical practitioners (or their locum medical practitioner acting under their written authorisation)"
              hide={prescribers.length === 0}
            >
              {_.sortBy(prescribers, ["lastName"]).map(prescriber => (
                <div key={prescriber.name} className={classes.prescriber}>
                  <NoticeCell label="Name" text={prescriber.name} />
                  <NoticeCell label="HPI -CPN" text={prescriber.hpiCpn} />
                </div>
              ))}
            </Section>

            <Section
              label="Except for prescriptions or supplies given by, or authorised in writing by, medical practitioners at"
              hide={otherServiceProviders.length === 0}
            >
              {otherServiceProviders.map(otherServiceProvider => (
                <Contact
                  key={otherServiceProvider.name}
                  {...otherServiceProvider}
                />
              ))}
            </Section>
            <Section
              label="Except for supplies (dispensings) given by the following pharmacies"
              hide={pharmacies.length === 0}
            >
              {pharmacies.map(pharmacy => (
                <Contact key={pharmacy.name} {...pharmacy} />
              ))}
            </Section>
          </Card>
        );
      })}
    </div>
  );
};

const NoticeCell: React.FC<{
  label: string;
  text?: string | null;
  format?: (text: string) => string;
}> = ({ label, text, format }) =>
  !text ? null : (
    <div style={{ marginRight: "2em" }}>
      <Typography color="textSecondary" variant="caption">
        {label}
      </Typography>
      <Typography>{format ? format(text) : text}</Typography>
    </div>
  );

const Section: React.FC<{
  label: string;
  hide?: boolean;
  children: any;
}> = ({ label, hide = false, children }) =>
  hide ? null : (
    <div
      style={{ display: "flex", flexDirection: "column", marginBottom: "2em" }}
    >
      <Typography color="primary" variant="subtitle1">
        {label}
      </Typography>
      <div style={{ display: "flex", flexFlow: "row wrap" }}>{children}</div>
    </div>
  );

const Contact: React.FC<{
  name: string;
  address?: string | null;
  phoneNumber?: string | null;
}> = ({ name, address, phoneNumber }) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 280,
        marginRight: 20
      }}
    >
      <Typography style={{ marginBottom: 8 }}>{name}</Typography>
      {address && (
        <div
          style={{
            display: "flex",
            maxWidth: 300,
            alignItems: "center",
            marginBottom: 8
          }}
        >
          <Icon
            style={{ marginRight: 8 }}
            color="disabled"
            fontSize="small"
            name="location_on"
          />
          <Typography style={{ lineHeight: "0.9rem" }} variant="caption">
            {address}
          </Typography>
        </div>
      )}
      {phoneNumber && (
        <div
          style={{
            display: "flex",
            maxWidth: 300,
            alignItems: "center",
            marginBottom: 8
          }}
        >
          <Icon
            style={{ marginRight: 8 }}
            color="disabled"
            fontSize="small"
            name="call"
          />
          <Typography style={{ lineHeight: "0.9rem" }} variant="caption">
            {phoneNumber}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default RestrictionNotice;
